import React, { useState, useEffect } from 'react';
import Button from 'components/atoms/Button';
import Select from 'components/atoms/Select';
import { DeviceRemovalReason } from 'store/types';
import { DEVICE_REMOVAL_REASON_OPTIONS } from 'types/device-removal-reasons';

// Extend Window interface for jQuery
declare global {
  interface Window {
    $: (selector: string) => {
      modal: (action: 'show' | 'hide') => void;
      on: (event: string, handler: () => void) => void;
      off: (event: string, handler: () => void) => void;
    };
  }
}

interface RemoveDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: DeviceRemovalReason) => void;
  deviceType: 'HST' | 'POX';
  orderId: string;
  isLoading?: boolean;
}

export const RemoveDeviceModal: React.FC<RemoveDeviceModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  deviceType,
  orderId,
  isLoading = false,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');

  useEffect(() => {
    const modalElement = document.getElementById('removeDeviceModal');
    if (!modalElement) return;

    const $modal = window.$('#removeDeviceModal');

    if (isOpen) {
      $modal.modal('show');
    } else {
      $modal.modal('hide');
    }

    const handleHidden = () => {
      setSelectedReason('');
      onClose();
    };

    $modal.on('hidden.bs.modal', handleHidden);

    return () => {
      $modal.off('hidden.bs.modal', handleHidden);
    };
  }, [isOpen, onClose]);

  const handleConfirm = () => {
    if (selectedReason) {
      onConfirm(selectedReason as DeviceRemovalReason);
    }
  };

  const handleClose = () => {
    window.$('#removeDeviceModal').modal('hide');
  };

  const isConfirmDisabled = !selectedReason || isLoading;

  return (
    <div className="modal fade" id="removeDeviceModal" tabIndex={-1} role="dialog" aria-labelledby="removeDeviceModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-md" role="document" style={{ margin: 'calc(50vh - 250px) auto', zIndex: 1100 }}>
        <div className="modal-content" style={{ border: 'none' }}>
          <div className="modal-header" style={{ borderBottom: '1px solid #e5e7eb' }}>
            <h4 className="modal-title" id="removeDeviceModalLabel">
              Remove {deviceType} Device
            </h4>
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
              onClick={handleClose}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="modal-body">
            <div className="alert alert-warning d-flex align-items-start" style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '20px', marginRight: '12px', marginTop: '2px' }}>⚠️</div>
              <div style={{ color: 'black', fontSize: '14px', lineHeight: '1.5' }}>
                <strong>Warning:</strong> This action will remove the device from order #{orderId} and
                send it back to Patient Scheduling. This action cannot be undone.
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '8px', fontSize: '14px' }}>
                Please select the reason for device removal:
              </label>
              <Select
                name="deviceRemovalReason"
                value={selectedReason}
                handleChange={(e) => {
                  const value = e.target.value;
                  setSelectedReason(value);
                }}
                options={DEVICE_REMOVAL_REASON_OPTIONS.map(option => ({
                  key: option.value,
                  value: option.value,
                  name: option.label
                }))}
                defaultLabel="Select a reason..."
                disabled={isLoading}
                className="form-control"
                noDefault={false}
              />
              {selectedReason === DeviceRemovalReason.NOT_USED && (
                <small className="text-muted d-block mt-2" data-testid="not-used-hint">
                  The device went out but was never used: this check-out will not count toward its
                  usage.
                </small>
              )}
            </div>

            <div className="alert alert-info" style={{ padding: '12px 16px', marginBottom: '24px' }}>
              <div style={{ color: 'black', fontSize: '13px', lineHeight: '1.5' }}>
                💡 <strong>Note:</strong> The selected reason will be recorded in the order log
                and used for inventory management and reimbursement tracking.
              </div>
            </div>
          </div>

          <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
            <Button
              className="btn btn-secondary mr-3"
              onClick={handleClose}
              disabled={isLoading}
              label="Cancel"
            />
            <Button
              className="btn btn-danger"
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
              label={isLoading ? 'Removing Device...' : 'Remove Device'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
